import { CommonModule } from '@angular/common';
import { Component, Signal, OnInit, WritableSignal, signal, input, inject, InputSignal, model, computed, output } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SignalsStoreService } from '../../signals-store.service';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../../order.service';
import { SubscriptionsService } from '../../../settings/account/subscriptions/subscriptions.service';
import { NotificationService } from '../../notification/notification.service';
import { Subscription } from '../../../settings/account/subscriptions/subscriptions.types';
import { OnlyNumberDirective } from '../../directives/only-number.directive';
import { getProductSubscriptioninfo, openModalSkipProduct, openModalUnskipProduct } from '../../common/subscriptions.utils';
import { ModalContentService } from '../../modal-content/modal-content.service';
import { finalize } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { updateFirebaseOrderAndSubTrigger } from '../../common/order.utils';
import { getPurchaseLimitCeiling } from '../../common/purchase-limit.util';
import { FirebaseCrudService } from '../../firebase-crud.service';
import { EmptyMessageComponent } from '../../empty-message/empty-message.component';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ResolutionService } from '../../resolution.service';

@Component({
  selector: 'app-product-cart-preview',
  templateUrl: './product-cart-preview.component.html',
  styleUrl: './product-cart-preview.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    OnlyNumberDirective,
    EmptyMessageComponent,
    NgbTooltipModule,
  ]
})

export class ProductCartPreview implements OnInit {
  #resolutionService = inject(ResolutionService);
  signalsStoreService = inject(SignalsStoreService)
  orderService = inject(OrderService)
  #router = inject(Router);
  #subscriptionsService = inject(SubscriptionsService);
  #notificationService = inject(NotificationService);
  #modalContentService = inject(ModalContentService);
  #firebaseCrudService = inject(FirebaseCrudService);

  isInputValid!: boolean;
  previousValue!: number;
  selectedQuantity: WritableSignal<any> = signal(1);
  newQuantity = model(1);

  product: any = input(null, {
    alias: 'product',
    transform: (product: any) => {
      this.previousValue = product.quantity;
      this.newQuantity.set(product.quantity)
      this.isMoreThanTen.set(product.quantity >= 10);
      return product;
    }
  })

  // DEV-11183: el tooltip del badge se abre con tap en móvil y con hover en escritorio.
  isMobile = computed(() => this.#resolutionService.isMobile());

  // Pre-discount unit + line total, used to render the strikethrough when the
  // customer is on a discounted pricelist. Backend exposes `originalPrice` on
  // each line (see smc_api_front sale_order_v3 `line_original_price`).
  originalUnitPrice: Signal<number> = computed(() => Number(this.product()?.originalPrice ?? 0));

  hasPricelistDiscount: Signal<boolean> = computed(() => {
    const orig = Number(this.product()?.originalPrice ?? 0);
    return orig > 0 && orig > Number(this.product()?.price ?? 0);
  });

  originalTotalPrice: Signal<number> = computed(() =>
    Number(this.product()?.originalPrice ?? 0) * Number(this.product()?.quantity ?? 0));

  productKey: any = input(null, {
    alias: 'productKey'
  })

  allowModification: InputSignal<boolean> = input(true);

  isInOrder = input(false);

  isSkipped = input(false);

  isSubscription = input(false);

  isPreorder = input(false);

  showUpdateButton = computed(() => this.isMoreThanTen() && (this.previousValue !== +(this.newQuantity() || 0)))

  // DEV-11172: for pre-order lines the customer can only INCREASE the
  // quantity, never decrease it. The dropdown starts at the current cart
  // quantity so lower values aren't selectable.
  minSelectableQuantity = computed(() => this.isPreorder() ? (this.product()?.quantity || 1) : 1);

  isMoreThanTen = signal(false);

  /**
   * DEV-11416: the most this line can hold, under the purchase limit (shared with the
   * other line of the same variant) and under the stock left. Null when neither applies.
   */
  maxQuantity: Signal<number | null> = computed(() => this.#setUpMaxQuantity());

  /** The dropdown's options below 10, from the pre-order floor up to `maxQuantity`. */
  quantityOptions: Signal<number[]> = computed(() => {
    const max = Math.min(this.maxQuantity() ?? 9, 9);
    const options = [];
    for (let quantity = this.minSelectableQuantity(); quantity <= max; quantity++)
      options.push(quantity);
    return options;
  });

  allowsTenOrMore: Signal<boolean> = computed(() => (this.maxQuantity() ?? 10) >= 10);

  odooOrder = computed(() => this.orderService.odooOrder());

  firebaseOrder = computed(() => this.signalsStoreService.firebaseOrder());

  showProductStatusTag = environment.config.flows.order.showProductStatusTag;

  isProductInFirebaseOrder = computed(() => {
    const productKey = this.productKey();
    const firebaseOrder = this.firebaseOrder();
    if (!firebaseOrder) return false;
    return firebaseOrder.products?.[productKey]?.some((item: any) => item.variant.Id === this.product().variant.Id);
  });

  isProductInOrder = computed(() => {
    const productKey = this.productKey();
    const order = this.orderService.odooOrder();
    if (!order) return false;
    return order.products?.[productKey]?.some((item: any) => item.variant.Id === this.product().variant.Id);
  });

  deliveryInfo: InputSignal<any> = input.required();

  outUpdateOrder = output<void>();

  ngOnInit(): void {
    this.isInputValid = true;
  }

  prevRemoveCartProduct() {
    if (this.isProductInOrder()) {
      const odooOrder = this.odooOrder();
      if (!odooOrder?.id) return;
      this.orderService
        .deleteProductOrder(+odooOrder.id, this.product().lineId)
        .pipe(
          finalize(() => {
            updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService);
            if (this.isProductInFirebaseOrder())
              this.removeCartProduct();
          })
        )
        .subscribe()
    } else
      this.removeCartProduct();
  }

  removeCartProduct() {
    this.orderService.removeFirebaseProduct(this.productKey(), this.product(), this.signalsStoreService.firebaseOrder());
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/product-placeholder.webp';
  }

  goToCustomizeBox() {
    this.signalsStoreService.showCartPreview.set(false);
    this.#router.navigate([`/shop/custom-box/${this.productKey()}/${this.product().id}`]);
  }

  unskip() {
    const sub = this.#getSubscriptionInfoByProduct(this.product());
    if (!sub) return;
    openModalUnskipProduct({
      sub,
      modalContentService: this.#modalContentService,
      subscriptionsService: this.#subscriptionsService,
      orderService: this.orderService,
      router: this.#router,
      goToCustomizeBundle: false
    });
  }

  skip() {
    const sub = this.#getSubscriptionInfoByProduct(this.product());
    if (!sub) return;
    openModalSkipProduct({
      sub,
      modalContentService: this.#modalContentService,
      subscriptionsService: this.#subscriptionsService,
      orderService: this.orderService,
      router: this.#router,
      goToCustomizeBundle: false
    });
  }

  #getSubscriptionInfoByProduct(product: any): Subscription | null {
    const subscriptions = this.signalsStoreService.subscriptions();
    return getProductSubscriptioninfo(subscriptions, product.id);
  }

  onQuantityChange() {
    if (!this.newQuantity())
      return;

    if (this.newQuantity() === 10)
      return this.isMoreThanTen.set(true);

    this.#updateFirebaseQuantity();
  }

  validateQuantity() {
    if (!this.newQuantity())
      return this.#notificationService.show({ text: 'Please enter a valid quantity', type: 'error' });

    // DEV-11172: pre-order items can only be increased. Block any qty
    // below what's already on the order (e.g. via the free-text input
    // that appears once qty >= 10).
    if (this.isPreorder() && (this.newQuantity() || 0) < (this.product()?.quantity || 1)) {
      this.newQuantity.set(this.product()?.quantity || 1);
      return this.#notificationService.show({
        text: 'Pre-order items can only be increased. Contact customer service for other changes.',
        type: 'error',
      });
    }

    const maxQuantity = this.maxQuantity();
    if (maxQuantity !== null && (this.newQuantity() || 0) > maxQuantity) {
      this.newQuantity.set(maxQuantity);
      return this.#notificationService.show({
        text: `You can add up to ${maxQuantity} of this product to your order.`,
        type: 'error',
      });
    }

    this.#updateFirebaseQuantity();
  }

  #setUpMaxQuantity(): number | null {
    const line = this.product();
    if (!line) return null;

    const lineQuantity = line.quantity || 0;
    const order = this.#setUpEffectiveOrder();

    // A line added from the card carries no `purchaseLimit`; the order's line of the same
    // variant does.
    const variantId = this.#variantIdOf(line);
    const purchaseLimit = line.purchaseLimit ||
      [...order.products.common, ...order.products.subscription]
        .find(l => this.#variantIdOf(l) === variantId && l.purchaseLimit)?.purchaseLimit || null;

    let max = getPurchaseLimitCeiling({ ...line, purchaseLimit }, order);

    // Order lines bring the stock left beyond what the order already holds.
    const stockInfo = line.stockInfo?.[0];
    if (stockInfo && !line.bundle?.items?.length) {
      const stockLeft = Math.max(0, stockInfo.stock + (stockInfo.limit * -1));
      max = Math.min(max ?? Infinity, lineQuantity + stockLeft);
    }

    return max === null ? null : Math.max(max, lineQuantity);
  }

  /**
   * The lines as the cart shows them: a line pending in Firebase replaces the order's line
   * of the same variant and type, as the side checkout lists them.
   */
  #setUpEffectiveOrder(): { products: { common: any[], subscription: any[] } } {
    const odooOrder = this.odooOrder();
    const firebaseOrder = this.firebaseOrder();

    const linesOf = (key: 'common' | 'subscription'): any[] => {
      const pending: any[] = firebaseOrder?.products?.[key] || [];
      const pendingVariantIds = pending.map(l => this.#variantIdOf(l));
      const saved: any[] = (odooOrder?.products?.[key] || []).filter((l: any) => !pendingVariantIds.includes(this.#variantIdOf(l)));
      return [...pending, ...saved];
    };

    return { products: { common: linesOf('common'), subscription: linesOf('subscription') } };
  }

  #variantIdOf(line: any): number | null {
    return line?.variantId ?? line?.variant?.variantId ?? line?.variant?.id ?? null;
  }

  #updateFirebaseQuantity() {
    this.orderService.sendOrderToNavegoOnFbResponse.set(true);
    this.orderService
      .updateFirebaseProduct(
        this.productKey(),
        {
          ...this.product(),
          quantity: +this.newQuantity()
        },
        this.signalsStoreService.firebaseOrder()
      )?.then(() => {
        this.outUpdateOrder.emit();
      })
  }
}
